import React, { useEffect, useMemo, useState } from 'react'

import { NavMenuButton } from 'partials/MobileNav/types'
import { Page } from 'partials/Page'
import { UserProfilePreview } from 'partials/UserProfilePreview'

import { BlockedUser } from 'widgets/BlockedUser'
import { Box } from 'widgets/Box'
import { LoadingSpinner } from 'widgets/LoadingSpinner'
import { Modal } from 'widgets/Modal'
import { OptionItem } from 'widgets/OptionsDropdown/subcomponents/OptionItem'
import { Text } from 'widgets/Text'

import { useBlockUser } from 'hooks/useBlockUser'
import { useCurrentUser } from 'hooks/useCurrentUser'
import { useNavigateBackOrHome } from 'hooks/useNavigateBackOrHome'
import { useUser } from 'hooks/useUser'

import { openReportForm } from 'utils/links/links'
import { Toast } from 'utils/toast'

type Props = {
  route: any
  navigation: any
}

export const MemberProfilePage = ({ route, navigation }: Props) => {
  const navigateBackOrHome = useNavigateBackOrHome()
  const username = route?.params?.username

  const {
    data: user,
    error,
    isLoading: isUserLoading,
  } = useUser({
    username,
  })
  const { data: currentUser, isLoading: isCurrentUserLoading } = useCurrentUser()
  const [isCommentLoading, setIsCommentLoading] = useState(false)
  const isLoading = useMemo(
    () => isUserLoading || isCurrentUserLoading || isCommentLoading,
    [isCommentLoading, isCurrentUserLoading, isUserLoading],
  )

  const { blockUser } = useBlockUser(user?.id, user?.username)

  const memberIsBlocked: boolean =
    currentUser?.blockedUserIds && currentUser?.blockedUserIds?.length > 0
      ? currentUser.blockedUserIds.some((id) => id === user?.id)
      : false

  const currentUserIsBlocked: boolean =
    user?.blockedUserIds && user?.blockedUserIds?.length > 0
      ? user.blockedUserIds.some((id) => id === currentUser?.id)
      : false

  useEffect(() => {
    if (error || !username) {
      Toast.error('User not found', 'No trainer found with that username')
      navigateBackOrHome()
    }
  }, [username, error, navigateBackOrHome])

  const [isOptionsModalVisible, setIsOptionsModalVisible] = useState(false)
  const menuOptions = !memberIsBlocked ? ['Report account', 'Block account'] : ['Report account']

  const handleShowOptionsMenuPress = () => {
    setIsOptionsModalVisible(true)
  }

  const handleCloseOptionsMenuPress = () => {
    setIsOptionsModalVisible(false)
  }

  const optionsMenuButton: NavMenuButton = {
    iconName: 'options-menu',
    onPress: handleShowOptionsMenuPress,
  }

  const handleOnSelect = (index: number) => {
    setIsOptionsModalVisible(false)

    switch (index) {
      case 0: {
        openReportForm(currentUser, user)
        break
      }
      case 1: {
        blockUser.mutate()
        break
      }
      default:
        break
    }
  }

  return (
    <Page
      withNav
      navigation={navigation}
      withBack
      backgroundColor='white'
      title={!isLoading ? `${user?.firstName} ${user?.lastName}` : ''}
      navbarRightButtons={[optionsMenuButton]}
      isLoading={isLoading}
    >
      {isLoading && (
        <Box display='flex' flex={1} justifyContent='center' align='center' height='100%'>
          <LoadingSpinner />
          <Box padding={{ vertical: 'sm' }}>
            <Text>Loading...</Text>
          </Box>
        </Box>
      )}
      {!memberIsBlocked && !currentUserIsBlocked ? (
        <UserProfilePreview user={user || {}} setIsCommentLoading={setIsCommentLoading} />
      ) : (
        <BlockedUser user={user} isCurrentUser={!memberIsBlocked} />
      )}

      <Modal
        isVisible={isOptionsModalVisible}
        onClose={handleCloseOptionsMenuPress}
        variant='fullscreenModal'
      >
        {menuOptions.map((option, index) => {
          return (
            <OptionItem
              key={option}
              option={option}
              isCancelItem={false}
              onSelect={() => {
                handleOnSelect(index)
              }}
            />
          )
        })}
        <OptionItem isCancelItem onSelect={handleCloseOptionsMenuPress} />
      </Modal>
    </Page>
  )
}
